import React, { useState, useEffect, useCallback, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useNavigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import HeroSection from "./components/HeroSection";
import EssayPreview from "./components/EssayPreview";
// import EssayReviewer from './components/EssayReviewer';
import EssayEditor from "./components/EssayEditor";
import CollegeLogoCarousel from "./components/CollegeLogoCarousel";
import FeaturesSection from "./components/FeaturesSection";
import PricingSection from "./components/PricingSection";
import FAQSection from "./components/FAQSection";
import Footer from "./components/Footer";
import TestimonialsSection from "./components/TestimonialsSection";
import AuthModal from "./components/AuthModal";
import ScrollToTop from "./components/ScrollToTop";
import { AnonymousUserService } from "./utils/anonymousUser";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { PostHogProvider, usePostHog } from "./contexts/PostHogProvider";
import { QueryProvider } from "./providers/QueryProvider";
import ReviewerOnboardingModal from "./components/ReviewerOnboardingModal";
import { DatabaseService } from "./services/database";
import { IdentityService } from "./services/identity";
import type {
  UserComment,
  AIComment,
  ScoreReport,
} from "./components/EssayEditor";
import stripePromise from "./lib/stripe";
import { API_ENDPOINTS } from "./config/api";
import Layout from "./components/Layout";
import { Toaster, toast } from "sonner";

// Lazy load heavy components for better performance and preloading
const ApplicantDashboard = lazy(() => import("./components/ApplicantDashboard"));
const ReviewerDashboard = lazy(() => import("./components/ReviewerDashboard"));
const MatchPage = lazy(() => import("./components/MatchPage"));
const ReviewerSection = lazy(() => import("./components/ReviewerSection"));
const PaymentSuccess = lazy(() => import("./components/PaymentSuccess"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./components/TermsOfService"));
const AuthRouter = lazy(() => import("./components/AuthRouter"));
const Onboarding = lazy(() => import("./components/Onboarding"));
const ReviewInterface = lazy(() => import("./components/ReviewInterface"));
const NotFound = lazy(() => import("./components/NotFound"));
const PublicEssayView = lazy(() => import("./components/PublicEssayView"));
const Scholarships = lazy(() => import("./components/Scholarships"));
const Colleges = lazy(() => import("./components/Colleges"));

type Comment = UserComment | AIComment;
type Analysis = {
  timestamp: string;
  name: string;
  prompt: string;
  content: string;
  comments: Comment[];
  summary: string;
  report: ScoreReport | null;
};
type StoredAnalysis = {
  timestamp: string;
  name: string;
  prompt: string;
  content: string;
  comments: Array<
    (Omit<UserComment, "createdAt"> & { createdAt: string }) | AIComment
  >;
  summary: string;
  report: ScoreReport | null;
};

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

// Onboarding Protected Route component - requires both authentication and completed onboarding
const OnboardingProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading, userOnboarded } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/" />;
  }

  // If user hasn't completed onboarding, redirect to onboarding
  if (userOnboarded === false) {
    return <Navigate to="/onboarding" />;
  }

  return <>{children}</>;
};

// Reviewee Protected Route component - requires user to have 'reviewee' role
const RevieweeProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading, identity, identityLoading } = useAuth();

  if (loading || identityLoading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/" />;
  }

  // If user is not a reviewee, redirect to reviewer-dashboard
  if (identity && !identity.isReviewee) {
    return <Navigate to="/reviewer-dashboard" />;
  }

  return <>{children}</>;
};

function LandingPage({
  onReviewClick,
  onFreeSignUp,
  user,
}: {
  onReviewClick: () => void;
  onFreeSignUp: () => void;
  user: any;
}) {
  return (
    <>
      <main className="dotted-background">
        <HeroSection onReviewClick={onReviewClick} />
        <EssayPreview />
        <CollegeLogoCarousel />
        <FeaturesSection
          onStartReview={onReviewClick}
          onViewPricing={() => {
            const pricingSection = document.getElementById("pricing-section");
            if (pricingSection) {
              pricingSection.scrollIntoView({ behavior: "smooth" });
            }
          }}
        />
        <ReviewerSection onReviewerSignUp={onReviewClick} />
        <div id="pricing-section">
          <PricingSection onFreeSignUp={onFreeSignUp} />
        </div>
        <FAQSection />
      </main>
      <Footer />
    </>
  );
}

function AppRoutes() {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isReviewerOnboardingModalOpen, setIsReviewerOnboardingModalOpen] =
    useState(false);
  const { user } = useAuth();
  const { captureEvent, capturePageView } = usePostHog();
  const navigate = useNavigate();

  const handleReviewClick = useCallback(() => {
    if (!user) {
      try {
        localStorage.removeItem("postLoginIntent");
      } catch (_) {}
      setIsAuthModalOpen(true);
      captureEvent("review_button_clicked", { isLoggedIn: false });
    } else {
      captureEvent("review_button_clicked", { isLoggedIn: true });
    }
  }, [user, captureEvent]);

  const handleFreeSignUp = useCallback(() => {
    setIsAuthModalOpen(true);
    captureEvent("free_signup_clicked");
  }, [captureEvent]);

  const handleOpenReviewerOnboarding = useCallback(() => {
    setIsReviewerOnboardingModalOpen(true);
  }, []);

  // Track page views
  useEffect(() => {
    capturePageView(window.location.pathname);
  }, [window.location.pathname, capturePageView]);

  // Loading fallback component
  const LoadingFallback = () => (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
    </div>
  );

  return (
    <>
      <Routes>
        <Route
          path="/auth-router"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <AuthRouter />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          element={
            <Layout
              user={user}
              onReviewClick={handleReviewClick}
              onOpenReviewerOnboarding={handleOpenReviewerOnboarding}
            />
          }
        >
          <Route
            path="/"
            element={
              <LandingPage
                onReviewClick={handleReviewClick}
                onFreeSignUp={handleFreeSignUp}
                user={user}
              />
            }
          />
          <Route
            path="/applicant-dashboard"
            element={
              <OnboardingProtectedRoute>
                <RevieweeProtectedRoute>
                  <Suspense fallback={<LoadingFallback />}>
                    <ApplicantDashboard />
                  </Suspense>
                </RevieweeProtectedRoute>
              </OnboardingProtectedRoute>
            }
          />
          <Route
            path="/reviewer-dashboard"
            element={
              <OnboardingProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <ReviewerDashboard />
                </Suspense>
              </OnboardingProtectedRoute>
            }
          />
          <Route 
            path="/match" 
            element={
              <Suspense fallback={<LoadingFallback />}>
                <MatchPage />
              </Suspense>
            } 
          />
          <Route
            path="/payment-success"
            element={
              <OnboardingProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <PaymentSuccess />
                </Suspense>
              </OnboardingProtectedRoute>
            }
          />
          <Route 
            path="/privacy" 
            element={
              <Suspense fallback={<LoadingFallback />}>
                <PrivacyPolicy />
              </Suspense>
            } 
          />
          <Route 
            path="/terms" 
            element={
              <Suspense fallback={<LoadingFallback />}>
                <TermsOfService />
              </Suspense>
            } 
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <Onboarding />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/review/:requestId"
            element={
              <OnboardingProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <ReviewInterface />
                </Suspense>
              </OnboardingProtectedRoute>
            }
          />
          <Route 
            path="/share/:shareToken" 
            element={
              <Suspense fallback={<LoadingFallback />}>
                <PublicEssayView />
              </Suspense>
            } 
          />
          <Route 
            path="/scholarships" 
            element={
              <Suspense fallback={<LoadingFallback />}>
                <Scholarships />
              </Suspense>
            } 
          />
          <Route 
            path="/colleges" 
            element={
              <Suspense fallback={<LoadingFallback />}>
                <Colleges />
              </Suspense>
            } 
          />
          <Route 
            path="*" 
            element={
              <Suspense fallback={<LoadingFallback />}>
                <NotFound />
              </Suspense>
            } 
          />
        </Route>
      </Routes>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => {
          setIsAuthModalOpen(false);
          captureEvent("auth_modal_closed");
        }}
        title="Welcome to CampusQuest"
        description="Sign in with Google to get personalized essay feedback powered by AI"
      />
      <ReviewerOnboardingModal
        isOpen={isReviewerOnboardingModalOpen}
        onClose={() => setIsReviewerOnboardingModalOpen(false)}
        onComplete={() => {
          setIsReviewerOnboardingModalOpen(false);
          // Redirect to reviewer-dashboard after completion
          navigate("/reviewer-dashboard");
        }}
      />
    </>
  );
}

function AppContent() {
  const [isProcessingUpgrade, setIsProcessingUpgrade] = useState(false);
  const { user } = useAuth();

  // After the user logs in, check if there was a pending Pro upgrade
  useEffect(() => {
    const initiateProCheckout = async () => {
      if (!user) return;
      const pending = localStorage.getItem("upgradePro");
      if (pending !== "true") return;

      // Remove the flag immediately to prevent redirect loops
      localStorage.removeItem("upgradePro");
      
      setIsProcessingUpgrade(true);

      try {
        const response = await fetch(API_ENDPOINTS.createCheckoutSession, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: user.id,
            line_items: [
              {
                price: process.env.REACT_APP_STRIPE_PRICE_ID,
                quantity: 1,
              },
            ],
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.detail || "Failed to create checkout session"
          );
        }

        const { sessionId } = await response.json();

        const stripe = await stripePromise;
        if (stripe) {
          const { error } = await stripe.redirectToCheckout({ sessionId });
          if (error) {
            throw error;
          }
        }
      } catch (error) {
        console.error("Error initiating Stripe checkout:", error);
        setIsProcessingUpgrade(false);
      }
    };

    initiateProCheckout();
  }, [user]);

  // Show loading screen while processing upgrade
  if (isProcessingUpgrade) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-gray-600">Redirecting to checkout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white w-full overflow-x-hidden">
      <Toaster position="bottom-right" richColors closeButton />
      <Router>
        <AppRoutes />
      </Router>
    </div>
  );
}

// New EssayWorkspace component that combines Editor and Reviewer
function EssayWorkspace() {
  const [essayContent, setEssayContent] = useState("");
  const [essayPrompt, setEssayPrompt] = useState("");
  const [essayTitle, setEssayTitle] = useState("");
  const [savedComments, setSavedComments] = useState<Comment[]>([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedReport, setSavedReport] = useState<ScoreReport | null>(null);
  const [analysisHistory, setAnalysisHistory] = useState<Analysis[]>([]);
  const [selectedAnalysisIndex, setSelectedAnalysisIndex] = useState<
    number | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { user } = useAuth();
  const { id: essayId } = useParams();
  const navigate = useNavigate();

  // Handle unsaved changes before window/tab close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Load essay content when component mounts
  useEffect(() => {
    const loadData = async () => {
      if (!essayId || !user?.id) return;

      try {
        const essay = await DatabaseService.getEssay(essayId);

        if (essay) {
          console.log("Loading essay from database:", essay);
          console.log("Essay content loaded:", essay.content);
          setEssayContent(essay.content || "");
          setEssayTitle(essay.title || "");

          // Set prompt from the database field first
          setEssayPrompt(essay.prompt || "");

          // If there's feedback data, parse it
          if (essay.feedback) {
            try {
              const feedbackData = JSON.parse(essay.feedback);
              // Only use feedback prompt if database prompt is empty (for backwards compatibility)
              if (!essay.prompt && feedbackData.prompt) {
                setEssayPrompt(feedbackData.prompt);
              }
              // Load saved comments and summary from the latest analysis
              if (feedbackData.history && feedbackData.history.length > 0) {
                const history = feedbackData.history as StoredAnalysis[];
                // Convert stored history to Analysis type
                const convertedHistory = history.map((analysis) => ({
                  ...analysis,
                  comments: analysis.comments.map((comment) => {
                    if (comment.type === "user" && comment.createdAt) {
                      return {
                        ...comment,
                        createdAt: new Date(comment.createdAt),
                      } as UserComment;
                    }
                    return comment;
                  }) as Comment[],
                }));

                setAnalysisHistory(convertedHistory);

                // Set the latest analysis as current
                const latestAnalysis =
                  convertedHistory[convertedHistory.length - 1];
                setSavedComments(latestAnalysis.comments);
                setSavedSummary(latestAnalysis.summary);
                setSavedReport(latestAnalysis.report || null);

                // Debug: Log when report is loaded from database
                if (latestAnalysis.report) {
                  console.log(
                    "Loading saved report from database:",
                    latestAnalysis.report
                  );
                } else {
                  console.log("No report found in saved analysis");
                }
              }
            } catch (err) {
              console.error("Error parsing feedback data:", err);
            }
          }
        }
      } catch (err) {
        console.error("Error loading essay data:", err);
        toast.error("Failed to load essay. Please try refreshing the page.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [essayId, user?.id]);

  const handleSave = async (
    content: string,
    comments: Comment[],
    summary: string,
    prompt: string,
    title: string,
    report: ScoreReport | null
  ) => {
    if (!essayId || !user?.id) return;

    try {
      // Add new analysis to history
      const newAnalysis: Analysis = {
        timestamp: new Date().toISOString(),
        name: `Analysis #${analysisHistory.length + 1}`,
        prompt,
        content,
        comments,
        summary,
        report,
      };

      const updatedHistory = [...analysisHistory, newAnalysis];

      await saveHistoryToDatabase(
        content,
        updatedHistory,
        prompt,
        title,
        report
      );
    } catch (err) {
      console.error("Error saving essay:", err);
      toast.error("Failed to save essay. Please try again.");
    }
  };

  const saveHistoryToDatabase = async (
    content: string,
    history: Analysis[],
    prompt: string,
    title: string,
    report: ScoreReport | null
  ) => {
    // Create feedback string that includes all data
    const storedHistory: StoredAnalysis[] = history.map((analysis) => ({
      ...analysis,
      comments: analysis.comments.map((comment) => {
        if (comment.type === "user") {
          const userComment = comment as UserComment;
          return {
            ...userComment,
            createdAt: userComment.createdAt.toISOString(),
          };
        }
        return comment;
      }),
    }));

    const feedback = JSON.stringify({
      history: storedHistory,
      prompt, // Save current prompt in root for reference
    });

    const updatedEssay = await DatabaseService.updateEssay(essayId!, {
      content,
      feedback,
      title: title.trim(),
      status:
        history[history.length - 1].comments.length > 0 ||
        history[history.length - 1].summary
          ? "reviewed"
          : "draft",
    });

    if (!updatedEssay) {
      toast.error("Failed to save essay. Please try again.");
    } else {
      setAnalysisHistory(history);
    }
  };

  const handleSaveTitle = async (title: string) => {
    if (!essayId || !user?.id) return;
    try {
      await DatabaseService.updateEssay(essayId, { title });
    } catch (err) {
      console.error("Error saving title:", err);
      toast.error("Failed to save title. Please try again.");
    }
  };

  // Essay-only save function (for auto-save) - doesn't create analysis entries or hit limits
  const handleEssayOnlySave = async (
    content: string,
    prompt: string,
    title: string
  ) => {
    if (!essayId || !user?.id) return;
    try {
      console.log("handleEssayOnlySave called with content:", content);
      // Only save the essay content, title, and prompt without creating analysis
      const result = await DatabaseService.updateEssay(essayId, {
        content,
        prompt,
        title,
      });
      console.log("DatabaseService.updateEssay result:", result);

      // Don't update local state to avoid re-renders and focus loss during typing
    } catch (err) {
      console.error("Error auto-saving essay:", err);
      // Don't show user error for auto-save failures to avoid interrupting workflow
    }
  };

  const handleDeleteAnalysis = async (indexToDelete: number) => {
    if (!essayId || !user?.id) return;

    try {
      const updatedHistory = analysisHistory.filter(
        (_, index) => index !== indexToDelete
      );

      // If we're deleting the currently selected analysis, clear the selection
      if (selectedAnalysisIndex === indexToDelete) {
        setSelectedAnalysisIndex(null);
        // If there are remaining analyses, select the latest one
        if (updatedHistory.length > 0) {
          const latestAnalysis = updatedHistory[updatedHistory.length - 1];
          setSavedComments(latestAnalysis.comments);
          setSavedSummary(latestAnalysis.summary);
          setSavedReport(latestAnalysis.report || null);
          setSelectedAnalysisIndex(updatedHistory.length - 1);
        } else {
          setSavedComments([]);
          setSavedSummary("");
          setSavedReport(null);
        }
      } else if (
        selectedAnalysisIndex &&
        selectedAnalysisIndex > indexToDelete
      ) {
        // If we're deleting an analysis before the selected one, update the index
        setSelectedAnalysisIndex(selectedAnalysisIndex - 1);
      }

      await saveHistoryToDatabase(
        essayContent,
        updatedHistory,
        essayPrompt,
        essayTitle,
        savedReport
      );
    } catch (err) {
      console.error("Error deleting analysis:", err);
      toast.error("Failed to delete analysis. Please try again.");
    }
  };

  const handleRenameAnalysis = async (index: number, newName: string) => {
    if (!essayId || !user?.id || !newName.trim()) return;

    try {
      const updatedHistory = analysisHistory.map((analysis, i) =>
        i === index ? { ...analysis, name: newName.trim() } : analysis
      );

      await saveHistoryToDatabase(
        essayContent,
        updatedHistory,
        essayPrompt,
        essayTitle,
        savedReport
      );
    } catch (err) {
      console.error("Error renaming analysis:", err);
      toast.error("Failed to rename analysis. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
          <p className="text-gray-600">Loading your essay...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto">
        <EssayEditor
          initialContent={essayContent}
          initialPrompt={essayPrompt}
          initialTitle={essayTitle}
          initialComments={savedComments}
          initialSummary={savedSummary}
          initialReport={savedReport}
          analysisHistory={analysisHistory}
          onSave={handleSave}
          onEssayOnlySave={handleEssayOnlySave}
          onSaveTitle={handleSaveTitle}
          onDeleteAnalysis={handleDeleteAnalysis}
          onRenameAnalysis={handleRenameAnalysis}
          onUnsavedChanges={setHasUnsavedChanges}
        />
      </div>
    </div>
  );
}

// Add EssayEditor props interface
export interface EssayEditorProps {
  initialContent: string;
  initialPrompt: string;
  initialTitle: string;
  initialComments: Comment[];
  initialSummary: string;
  initialReport: ScoreReport | null;
  analysisHistory: Analysis[];
  onSave: (
    content: string,
    comments: Comment[],
    summary: string,
    prompt: string,
    title: string,
    report: ScoreReport | null
  ) => Promise<void>;
  onEssayOnlySave: (
    content: string,
    prompt: string,
    title: string
  ) => Promise<void>;
  onSaveTitle: (title: string) => Promise<void>;
  onDeleteAnalysis: (indexToDelete: number) => Promise<void>;
  onRenameAnalysis: (index: number, newName: string) => Promise<void>;
  onUnsavedChanges?: (hasUnsavedChanges: boolean) => void;
  essayId?: string; // Optional essay ID for permission checking
  overridePermission?: "read" | "comment" | "edit" | "owner"; // Optional permission override
}

function App() {
  // Initialize anonymous user cleanup
  React.useEffect(() => {
    AnonymousUserService.initializeCleanup();
  }, []);

  return (
    <HelmetProvider>
      <QueryProvider>
        <AuthProvider>
          <PostHogProvider>
            <AppContent />
          </PostHogProvider>
        </AuthProvider>
      </QueryProvider>
    </HelmetProvider>
  );
}

export default App;
